import {
  Close as CloseIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material'
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  Typography,
  IconButton,
  Box,
  Button,
} from '@mui/material'
import React from 'react'

import { useTranslation } from 'react-i18next'
import { FilterConfig, SelectedFilters } from '@/models/filters'

interface FilterDrawerProps {
  open: boolean
  onClose: () => void
  filterConfig: FilterConfig
  selectedFilters: SelectedFilters
  onFilterChange: (
    groupName: string,
    optionKey: string,
    optionLabel: string,
    isSelected: boolean,
  ) => void
  onClearAll: () => void
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  filterConfig,
  selectedFilters,
  onFilterChange,
  onClearAll,
}) => {
  const getTotalActiveFilters = (): number => {
    return Object.values(selectedFilters).reduce(
      (total, group) => total + Object.keys(group).length,
      0,
    )
  }
  const { t } = useTranslation('common')
  const activeCount = getTotalActiveFilters()

  return (
    <Drawer
      data-testid="filter-drawer"
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        width: 320,
        '& .MuiDrawer-paper': {
          backgroundColor: '#fff', // white
          width: 240, // keep same width for the paper
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <FilterIcon sx={{ color: 'text.primary' }} />
            <ListItemText secondary={t('general.filters')} />
            {activeCount > 0 && (
              <Box
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  minWidth: 20,
                }}
              >
                {activeCount}
              </Box>
            )}
          </Typography>
          <IconButton
            data-testid="filter-drawer-close-button"
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1 }}>
        {filterConfig.groups.map((group, groupIndex) => (
          <React.Fragment key={group.id}>
            <ListItem>
              <ListItemText
                primary={group.label}
                sx={{
                  variant: 'subtitle1',
                  fontWeight: 'medium',
                  color: 'text.primary',
                }}
              />
            </ListItem>
            {group.options.map((option) => {
              const isSelected = Boolean(
                selectedFilters[group.id]?.[option.key],
              )

              return (
                <ListItem
                  key={`${group.id}-${option.key}`}
                  data-testid={`filter-option-${group.id}-${option.key}`}
                  onClick={() =>
                    onFilterChange(
                      group.id,
                      option.key,
                      option.label,
                      !isSelected,
                    )
                  }
                  sx={{
                    pl: 3,
                    cursor: 'pointer',
                    color: 'text.primary',
                    minWidth: '40px',
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                      sx={{ color: 'text.primary' }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={option.label} />
                </ListItem>
              )
            })}
            {groupIndex < filterConfig.groups.length - 1 && <Divider />}
          </React.Fragment>
        ))}
        <Divider />
        <Box sx={{ p: 2 }}>
          {activeCount > 0 && (
            <Button
              data-testid="filter-drawer-clear-all-button"
              color="inherit"
              variant="outlined"
              size="small"
              onClick={onClearAll}
              sx={{ mb: 2 }}
              fullWidth
            >
              {t('common:general.clearFilters')}
            </Button>
          )}
        </Box>
      </List>
    </Drawer>
  )
}
